import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class DeliveryDateService {
  /**
   * Calculate delivery date based on business days
   * @param businessDays Number of business days to add (default: 3-5 days)
   * @param startDate Starting date (default: today)
   * @returns Formatted delivery date string
   */
  calculateDeliveryDate(
    businessDays: number = 3,
    startDate: Date = new Date(),
  ): string {
    const date = new Date(startDate);
    let daysAdded = 0;

    while (daysAdded < businessDays) {
      date.setDate(date.getDate() + 1);
      // Skip weekends (Saturday = 6, Sunday = 0)
      const dayOfWeek = date.getDay();
      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
        daysAdded++;
      }
    }

    return this.formatDate(date);
  }

  /**
   * Calculate delivery date range (e.g., "3-5 business days")
   * @param minDays Minimum business days
   * @param maxDays Maximum business days
   * @returns Object with min and max dates
   */
  calculateDeliveryDateRange(
    minDays: number = 3,
    maxDays: number = 5,
  ): { minDate: string; maxDate: string; rangeText: string } {
    const minDate = this.calculateDeliveryDate(minDays);
    const maxDate = this.calculateDeliveryDate(maxDays);
    const rangeText = `${minDate} - ${maxDate}`;

    return { minDate, maxDate, rangeText };
  }

  /**
   * Format date to display format (dd MMM or dd/MM/yyyy)
   * @param date Date to format
   * @param format Format type ('short' or 'long')
   * @returns Formatted date string
   */
  formatDate(date: Date, format: 'short' | 'long' = 'short'): string {
    const day = date.getDate();
    const monthNames = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();

    if (format === 'short') {
      return `${day} ${month}`;
    } else {
      const monthPadded = String(date.getMonth() + 1).padStart(2, '0');
      const dayPadded = String(day).padStart(2, '0');
      return `${dayPadded}/${monthPadded}/${year}`;
    }
  }

  /**
   * Get delivery text for UI display
   * @param businessDays Number of business days
   * @returns Formatted text like "Get Delivery by 15 Jan"
   */
  getDeliveryText(businessDays: number = 3): string {
    const deliveryDate = this.calculateDeliveryDate(businessDays);
    return `Get Delivery by ${deliveryDate}`;
  }

  /**
   * Get pickup text for UI display
   * @param businessDays Number of business days
   * @returns Formatted text like "Pickup after 15 Jan"
   */
  getPickupText(businessDays: number = 2): string {
    const pickupDate = this.calculateDeliveryDate(businessDays);
    return `Pickup after ${pickupDate}`;
  }

  /**
   * Calculate delivery date from order date
   * @param orderDate Order creation date
   * @param businessDays Business days for delivery
   * @returns Delivery date
   */
  getDeliveryDateFromOrder(
    orderDate: string | Date,
    businessDays: number = 3,
  ): string {
    const startDate =
      typeof orderDate === 'string' ? new Date(orderDate) : orderDate;
    return this.calculateDeliveryDate(businessDays, startDate);
  }
}
